import { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alpha2Code, Alpha3Code } from 'i18n-iso-countries'

import { FlagAvatar } from '@percent/lemonade'
import countries, { loadCountryData } from '@percent/workplace-giving/i18n/countries'
import { WorldFlagAvatar } from '@percent/workplace-giving/common/components/WorldFlagAvatar/WorldFlagAvatar'
import { getShortLanguage } from '@percent/workplace-giving/utils/getShortLanguage'

type UseCountryOptionsParams = {
  countryCodes: Alpha3Code[] | string[]
  includeWorldOption?: boolean
}

export type CountryOption = {
  value: string
  label: string
  prefix: JSX.Element
}

type UseCountryOptionsResult = CountryOption[]

/**
 * Country name overrides to customize how certain countries are displayed
 * Maps Alpha3 country codes to their preferred display names
 */
export const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  TWN: 'Taiwan',
  CHN: 'China Mainland',
  MAC: 'Macau SAR',
  HKG: 'Hong Kong SAR'
}

/**
 * Gets a country name with overrides applied
 * @param countryCode - Alpha3 country code
 * @param language - Language code (e.g. 'en', 'pl')
 * @returns Country name with overrides applied, or default name if no override exists
 */
export function getCountryNameWithOverride(countryCode: Alpha3Code | string, language: string): string {
  if (COUNTRY_NAME_OVERRIDES[countryCode]) {
    return COUNTRY_NAME_OVERRIDES[countryCode]
  }

  const defaultName = countries.getName(countryCode as Alpha3Code, language) || countryCode

  return defaultName
}

/**
 * Custom hook that creates a localized an d sorted list of country options
 * for use in Select components.
 *
 * @param countryCodes - Array of Alpha3 country codes to include in the options
 * @param includeWorldOption - Whether to include a "World" option at the beginning (default: true)
 * @returns Array of SelectOption objects with localized country names, sorted alphabetically
 *
 * @example
 * const countryOptions = useCountryOptions({
 *   countryCodes: volunteeringAllowedOrgCountriesAlpha3,
 *   includeWorldOption: true
 * })
 */
export function useCountryOptions({
  countryCodes,
  includeWorldOption = true
}: UseCountryOptionsParams): UseCountryOptionsResult {
  const { t, i18n } = useTranslation()
  const [dataLoaded, setDataLoaded] = useState(false)

  const shortLanguage = getShortLanguage(i18n)

  // Load country locale data for current language
  useEffect(() => {
    let isMounted = true

    loadCountryData(shortLanguage).then(() => {
      if (isMounted) {
        setDataLoaded(true)
      }
    })

    return () => {
      isMounted = false
    }
  }, [shortLanguage])

  const options = useMemo(() => {
    const countryOptions = countryCodes
      .map(code => {
        const countryCode = code as Alpha3Code
        const displayName = getCountryNameWithOverride(countryCode, shortLanguage)

        return {
          value: code,
          label: displayName,
          prefix: <FlagAvatar code={countries.alpha3ToAlpha2(countryCode) as Alpha2Code} />
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language))

    if (includeWorldOption) {
      return [
        {
          value: '',
          label: t('workplace_giving.search.world'),
          prefix: <WorldFlagAvatar />
        },
        ...countryOptions
      ]
    }

    return countryOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodes, shortLanguage, includeWorldOption, t, dataLoaded])

  return options
}
